<template>
  <div class="shipping-dashboard-page mt-3">
    <CCard class="shipping-dashboard-card border-0 shadow-sm mt-3">
      <CCardBody>
        <div class="filter-bar mb-3">
          <div class="page-meta">
            <h2 class="page-title">Shipping Dashboard</h2>
            <p class="page-subtitle">
              <span>Dispatch and shipment execution snapshot</span>
              <span class="meta-dot" aria-hidden="true">·</span>
              <span>Last updated: {{ lastUpdatedLabel }}</span>
            </p>
          </div>
          <div class="filter-actions">
            <CButton
              class="action-btn scan-btn"
              :to="{ name: 'ScannerReadyToShip' }"
              title="Open Ready-To-Ship Scanner"
              aria-label="Open scanner"
            >
              <span class="scan-icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 7V5a2 2 0 0 1 2-2h2" />
                  <path d="M17 3h2a2 2 0 0 1 2 2v2" />
                  <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
                  <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                </svg>
              </span>
              <span>Scan</span>
            </CButton>
            <CButton
              class="action-btn refresh-btn"
              :class="{ 'is-loading': isLoading }"
              :disabled="isLoading"
              title="Refresh"
              aria-label="Refresh dashboard"
              @click="fetchDashboardData"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="refresh-btn__icon" aria-hidden="true">
                <polyline points="23 4 23 10 17 10" />
                <polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
              <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
            </CButton>
          </div>
        </div>

        <CAlert v-if="errorMessage" color="danger" show>
          {{ errorMessage }}
        </CAlert>
        <CAlert v-if="submitSuccessMessage" color="success" show>
          {{ submitSuccessMessage }}
        </CAlert>
        <CAlert v-if="submitErrorMessage" color="danger" show>
          {{ submitErrorMessage }}
        </CAlert>

        <CRow class="tight-row tiles-row pt-2">
          <CCol
            v-for="(card, index) in summaryCards"
            :key="card.key"
            xl="3"
            lg="4"
            sm="6"
            class="mb-2"
          >
            <CCard :class="['h-100 border-0 tile-card', summaryCardClass(card.key, index)]">
              <CCardBody class="tile-body">
                <span class="tile-icon" aria-hidden="true">
                  <CIcon :name="tileIcon(card.key)" size="lg" />
                </span>
                <div class="tile-value">{{ card.value }}</div>
                <div class="tile-label">{{ card.label }}</div>
                <button
                  v-if="cardActionLabel(card.key)"
                  type="button"
                  class="tile-action"
                  :disabled="isCardActionLoading(card.key) || isCardActionDisabled(card.key, card.value)"
                  @click="onCardAction(card.key)"
                >
                  {{ isCardActionLoading(card.key) ? 'Processing...' : cardActionLabel(card.key) }}
                </button>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        <div class="reports-section pt-3">
          <div class="report-tabs" role="tablist">
            <button
              v-for="tab in reportTabs"
              :key="tab.key"
              type="button"
              role="tab"
              class="report-tab"
              :class="{ 'is-active': tab.key === activeReport }"
              :aria-selected="tab.key === activeReport"
              @click="activeReport = tab.key"
            >
              <span class="tab-label">{{ tab.label }}</span>
              <span v-if="!isLoading && tab.items.length" class="tab-count">{{ tab.items.length }}</span>
            </button>
          </div>

          <div v-if="showFilterBar" class="dashboard-filter-bar">
            <div class="dashboard-filter-bar__search">
              <span class="dashboard-filter-bar__search-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="11" cy="11" r="7" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              <input
                v-model="searchTerm"
                type="text"
                class="dashboard-filter-bar__input"
                placeholder="Search across all visible columns…"
                aria-label="Search rows"
              >
              <button
                v-if="searchTerm"
                type="button"
                class="dashboard-filter-bar__clear-input"
                aria-label="Clear search"
                @click="searchTerm = ''"
              >×</button>
            </div>

            <div class="dashboard-filter-bar__org">
              <AppDropdown
                v-model="organisationFilter"
                :options="organisationOptions"
                placeholder="All Organisations"
                :searchable="true"
                search-placeholder="Search organisation…"
              />
            </div>

            <button
              v-if="searchTerm || organisationFilter"
              type="button"
              class="dashboard-filter-bar__clear"
              @click="clearFilters"
            >Clear filters</button>
          </div>

          <BaseTable
            :accent="activeTab.accent"
            :items="filteredItems"
            :columns="activeTab.columns"
            :loading="isLoading"
            :empty-text="filteredEmptyText"
            :show-count="false"
            paginated
            :page-size="25"
          >
            <template #cell-courier="{ row }">
              {{ row.courier || row.shipping_method || '—' }}
            </template>
            <template #cell-shipping_method="{ value }">
              <span v-if="value" :class="['ship-badge', shippingMethodBadgeClass(value)]">
                {{ shippingMethodLabel(value) }}
              </span>
              <span v-else class="ship-badge ship-badge--gray">—</span>
            </template>
            <template #cell-shipping_status="{ value }">
              <span v-if="value" :class="['ship-badge', shippingStatusBadgeClass(value)]">
                <svg
                  class="ship-badge__icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                  v-html="shippingStatusIcon(value)"
                ></svg>
                {{ shippingStatusLabel(value) }}
              </span>
              <span v-else class="ship-badge ship-badge--gray">—</span>
            </template>
            <template #cell-count="{ row }">
              {{ row.count != null ? row.count : (row.items != null ? row.items : 0) }}
            </template>
            <template #cell-actions="{ row, index }">
              <CButton
                color="primary"
                size="sm"
                class="row-action-btn"
                :disabled="isRowActionLoading(index)"
                @click="markRowAsShipped(row, index)"
              >
                {{ isRowActionLoading(index) ? 'Marking...' : 'Mark As Shipped' }}
              </CButton>
            </template>
          </BaseTable>
        </div>

        <CCard class="tracking-card mt-4 border-0 shadow-sm">
          <CCardBody>
            <h3 class="section-title">
              <span class="section-accent" aria-hidden="true"></span>
              Manual Update Tracking ID
            </h3>
            <CRow class="align-items-end mt-3">
              <CCol md="4">
                <CInput
                  label="Order ID"
                  placeholder="Enter Order ID"
                  v-model.trim="manualTracking.orderNumber"
                />
              </CCol>
              <CCol md="4">
                <CInput
                  label="Tracking ID"
                  placeholder="Enter Tracking ID"
                  v-model.trim="manualTracking.trackingId"
                />
              </CCol>
              <CCol md="4" class="pb-3">
                <CButton
                  color="primary"
                  class="action-btn w-100"
                  :disabled="isSubmittingTracking"
                  @click="submitTrackingUpdate"
                >
                  {{ isSubmittingTracking ? 'Updating...' : 'Update' }}
                </CButton>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>
      </CCardBody>
    </CCard>

    <AppConfirmDialog
      :visible="showDispatchConfirm"
      title="Generate Post Office dispatch mail?"
      :message="dispatchConfirmMessage"
      confirm-label="Send Mail"
      cancel-label="Cancel"
      busy-label="Sending…"
      :busy="isCardActionLoading('ready_to_ship_count')"
      variant="warning"
      :close-on-backdrop="!isCardActionLoading('ready_to_ship_count')"
      @confirm="confirmDispatch"
      @cancel="cancelDispatch"
    />
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'
import AppConfirmDialog from '@/components/AppConfirmDialog.vue'
import { tileIcon as tileIconFor } from '@/config/dashboardTileIcons'

const ORGANISATION_OPTIONS = [
  { value: 'map', label: 'Manakkal Agro Pharma' },
  { value: 'mdi', label: 'Manakkal Drugs India' },
  { value: 'mvp', label: 'Manakkal Veterinary Pharma' }
]

const CARD_DEFINITIONS = [
  { key: 'to_be_shipped_count', label: 'Order to Be Shipped' },
  { key: 'not_scanned_count', label: 'Pending to Scan/Pack' },
  { key: 'batchable_orders', label: 'Orders to be Batched' },
  { key: 'ready_to_ship_count', label: 'Ready to Ship (Scanned)' },
  { key: 'mailed_to_courier_count', label: 'Mailed To Courier' },
  { key: 'ready_to_ship_flipkart_count', label: 'Ready To Ship (Flipkart)' },
  { key: 'ready_to_ship_amazon_count', label: 'Ready To Ship (Amazon)' }
]

const ORDER_COLUMNS = [
  { key: 'order_number', label: 'Order Number' },
  { key: 'contact_name', label: 'Contact Name' },
  { key: 'item_count', label: 'Item Count', align: 'right' },
  { key: 'shipping_method', label: 'Shipping Method' },
  { key: 'weight', label: 'Weight', align: 'right' },
  { key: 'shipping_status', label: 'Shipping Status' }
]

// Shipping-status lifecycle buckets — the SINGLE source of truth shared by the
// badge colour and the badge icon, so the two can never drift. Every status the
// backend `shippingDashboard()` can emit must map to a bucket here; anything not
// listed falls through to 'unknown' (gray/help). Add new statuses in ONE place.
const SHIPPING_STATUS_BUCKET = {
  shipped: 'done',
  mailed_to_courier: 'done',
  ready_to_ship: 'active',
  pending: 'waiting',
  on_hold: 'waiting',
  in_queue: 'waiting',
  for_picking: 'waiting',
  picked: 'waiting',
  for_print: 'waiting',
  manifest_failed: 'problem',
  returned: 'problem'
}

const SHIPPING_STATUS_BADGE_CLASS = {
  done: 'ship-badge--green',    // completed
  active: 'ship-badge--blue',   // ready to ship
  waiting: 'ship-badge--amber', // queued / picking / printing
  problem: 'ship-badge--red',   // failed / returned
  unknown: 'ship-badge--gray'   // fallback
}

// Inline SVG inner-markup per bucket. stroke="currentColor" on the parent <svg>
// makes each icon inherit the badge's text colour, so icon + colour agree.
const SHIPPING_STATUS_ICON = {
  done: '<polyline points="20 6 9 17 4 12" />', // check
  active: '<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" />', // package
  waiting: '<circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />', // clock
  problem: '<path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />', // alert-triangle
  unknown: '<circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" />' // help-circle
}

export default {
  name: 'ShippingDashboard',
  components: { AppDropdown, AppConfirmDialog },
  data () {
    return {
      isLoading: false,
      isSubmittingTracking: false,
      activeCardActionKey: '',
      activeRowActionIndex: null,
      errorMessage: '',
      submitErrorMessage: '',
      submitSuccessMessage: '',
      lastUpdatedAt: null,
      dashboardPayload: {},
      activeReport: 'unscanned',
      showDispatchConfirm: false,
      searchTerm: '',
      organisationFilter: '',
      organisationOptions: ORGANISATION_OPTIONS,
      manualTracking: {
        orderNumber: '',
        trackingId: ''
      }
    }
  },
  computed: {
    summaryCards () {
      return CARD_DEFINITIONS.map((card) => ({
        ...card,
        value: this.readCount(card.key)
      }))
    },
    unscannedPickrrRows () {
      return this.asArray(this.dashboardPayload.unscanned_items_pickrr)
    },
    shippingSummaryRows () {
      return this.asArray(this.dashboardPayload.shipping_summary)
    },
    scannedRows () {
      return this.asArray(this.dashboardPayload.ready_to_ship)
    },
    markAsShippedRows () {
      return this.asArray(this.dashboardPayload.mark_as_shipped_table)
    },
    todayLabel () {
      return new Date().toDateString()
    },
    lastUpdatedLabel () {
      if (!this.lastUpdatedAt) return 'Not fetched yet'
      return this.lastUpdatedAt.toLocaleString()
    },
    reportTabs () {
      return [
        {
          key: 'unscanned',
          label: 'Unscanned Orders',
          accent: 'var(--brand-gradient)',
          items: this.unscannedPickrrRows,
          emptyText: 'No items',
          columns: [
            { key: 'order_date', label: 'Date', format: 'date' },
            { key: 'order_number', label: 'Order Number' },
            { key: 'contact_name', label: 'Contact Name' },
            { key: 'item_count', label: 'Item Count', align: 'right' },
            { key: 'shipping_method', label: 'Shipping Method' },
            { key: 'weight', label: 'Weight', align: 'right' },
            { key: 'shipping_status', label: 'Shipping Status' }
          ]
        },
        {
          key: 'scanned',
          label: 'Scanned Orders',
          accent: 'linear-gradient(180deg, #2e9d5f, #43b97a)',
          items: this.scannedRows,
          emptyText: 'No items',
          columns: ORDER_COLUMNS
        },
        {
          key: 'mark',
          label: 'Mark As Shipped',
          accent: 'linear-gradient(180deg, #c0392b, #e15543)',
          items: this.markAsShippedRows,
          emptyText: 'No items',
          columns: [
            { key: 'courier', label: 'Courier' },
            { key: 'count', label: 'Count', align: 'right' },
            { key: 'actions', label: 'Actions', sortable: false, align: 'center' }
          ]
        },
        {
          key: 'summary',
          label: 'Shipping Summary',
          accent: 'linear-gradient(180deg, #0ea5e9, #0369a1)',
          items: this.shippingSummaryRows,
          emptyText: 'No items',
          columns: [
            { key: 'shipping_method', label: 'Shipping Method' },
            { key: 'items', label: 'Items', align: 'right', formatter: (v) => (v == null || v === '' ? 0 : v) }
          ]
        },
      ]
    },
    activeTab () {
      return this.reportTabs.find((tab) => tab.key === this.activeReport) || this.reportTabs[0]
    },
    showFilterBar () {
      return ['unscanned', 'scanned', 'mark'].includes(this.activeReport)
    },
    tabSupportsOrgFilter () {
      const items = this.activeTab.items
      return Array.isArray(items) && items.length > 0 && Object.prototype.hasOwnProperty.call(items[0], 'order_number')
    },
    filteredItems () {
      let rows = Array.isArray(this.activeTab.items) ? this.activeTab.items : []

      if (!this.showFilterBar) return rows

      if (this.organisationFilter && this.tabSupportsOrgFilter) {
        const prefix = String(this.organisationFilter).toLowerCase()
        rows = rows.filter((row) => String(row.order_number || '').toLowerCase().startsWith(prefix))
      }

      const term = String(this.searchTerm || '').trim().toLowerCase()
      if (term) {
        const keys = (this.activeTab.columns || []).map((col) => col.key)
        rows = rows.filter((row) => keys.some((k) => String(row[k] == null ? '' : row[k]).toLowerCase().includes(term)))
      }

      return rows
    },
    filteredEmptyText () {
      if (this.searchTerm || (this.organisationFilter !== '' && this.organisationFilter !== null)) {
        return 'No rows match the current filters.'
      }
      return this.activeTab.emptyText
    },
    dispatchConfirmMessage () {
      const count = Number(this.readCount('ready_to_ship_count')) || 0
      if (count > 0) {
        const noun = count === 1 ? 'order' : 'orders'
        return `This will email ${count} ready-to-ship India Post ${noun} to the post office (one Excel sheet per organisation), download the same files as a ZIP, and mark the orders as shipped. This action can't be undone.`
      }
      return 'This will email today\'s India Post dispatch to the post office (one Excel sheet per organisation), download the same files as a ZIP, and mark those orders as shipped. This action can\'t be undone.'
    }
  },
  created () {
    this.fetchDashboardData()
  },
  methods: {
    tileIcon (key) {
      return tileIconFor(key)
    },
    shippingMethodLabel (value) {
      const map = {
        ip_registered_parcel: 'India Post Registered Parcel',
        ip_speed: 'Speed Post',
        dtdc: 'DTDC',
        pickrr: 'Pickrr',
        amazon: 'Amazon',
        flipkart: 'Flipkart',
        direct: 'Direct'
      }
      return map[value] || this.humanizeStatus(value)
    },
    shippingMethodBadgeClass (value) {
      // Carrier identity colour — stable hue per carrier for quick scanning,
      // not a good/bad signal (method is a category, not a lifecycle state).
      switch (value) {
        case 'ip_registered_parcel':
        case 'ip_speed':
          return 'ship-badge--blue'
        case 'dtdc':
          return 'ship-badge--indigo'
        case 'pickrr':
          return 'ship-badge--violet'
        case 'amazon':
          return 'ship-badge--amber'
        case 'flipkart':
          return 'ship-badge--sky'
        default:
          return 'ship-badge--gray'
      }
    },
    shippingStatusLabel (value) {
      return this.humanizeStatus(value)
    },
    shippingStatusBucket (value) {
      return SHIPPING_STATUS_BUCKET[value] || 'unknown'
    },
    shippingStatusBadgeClass (value) {
      return SHIPPING_STATUS_BADGE_CLASS[this.shippingStatusBucket(value)]
    },
    shippingStatusIcon (value) {
      return SHIPPING_STATUS_ICON[this.shippingStatusBucket(value)]
    },
    humanizeStatus (value) {
      if (value == null || value === '') return '—'
      return String(value)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
    },
    asArray (value) {
      return Array.isArray(value) ? value : []
    },
    clearFilters () {
      this.searchTerm = ''
      this.organisationFilter = ''
    },
    readCount (key) {
      const value = this.dashboardPayload[key]
      if (value === null || value === undefined || value === '') return 0
      return value
    },
    normalizePayload (payload) {
      if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
        return payload
      }

      return {}
    },
    summaryCardClass (key, index) {
      const toneMap = {
        to_be_shipped_count: 'tile-indigo',
        not_scanned_count: 'tile-amber',
        batchable_orders: 'tile-cyan',
        ready_to_ship_count: 'tile-emerald',
        mailed_to_courier_count: 'tile-ocean',
        ready_to_ship_flipkart_count: 'tile-violet',
        ready_to_ship_amazon_count: 'tile-crimson'
      }
      const fallbackTones = ['tile-indigo', 'tile-ocean', 'tile-emerald', 'tile-amber', 'tile-slate', 'tile-crimson']
      return toneMap[key] || fallbackTones[index % fallbackTones.length]
    },
    cardActionLabel (cardKey) {
      if (cardKey === 'ready_to_ship_count') return 'Generate PO Mail'
      if (cardKey === 'mailed_to_courier_count') return 'Mark As Shipped'
      if (cardKey === 'ready_to_ship_flipkart_count') return 'Mark As Shipped'
      if (cardKey === 'ready_to_ship_amazon_count') return 'Mark As Shipped'
      return ''
    },
    cardActionType (cardKey) {
      if (cardKey === 'mailed_to_courier_count') return 'india_post'
      if (cardKey === 'ready_to_ship_flipkart_count') return 'flipkart'
      if (cardKey === 'ready_to_ship_amazon_count') return 'amazon'
      return ''
    },
    isCardActionLoading (cardKey) {
      return this.activeCardActionKey === cardKey
    },
    isCardActionDisabled (cardKey, value) {
      const numericValue = Number(value || 0)
      if (Number.isNaN(numericValue)) return false
      return numericValue <= 0
    },
    isRowActionLoading (index) {
      return this.activeRowActionIndex === index
    },
    normalizeCourierValue (value) {
      if (!value) return ''
      return String(value)
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_')
    },
    deriveMarkShippedPayload (rawCourier) {
      const normalized = this.normalizeCourierValue(rawCourier)

      if (normalized.includes('pickrr')) {
        return {
          endpoint: '/api/orders/pickrr_mark_shipped',
          payload: { courier: 'pickrr' }
        }
      }

      if (normalized.includes('flipkart')) {
        return {
          endpoint: '/api/orders/mark_shipped',
          payload: { type: 'flipkart' }
        }
      }

      if (normalized.includes('amazon')) {
        return {
          endpoint: '/api/orders/mark_shipped',
          payload: { type: 'amazon' }
        }
      }

      if (
        normalized.includes('india_post') ||
        normalized.includes('india') ||
        normalized.includes('ip_') ||
        normalized.includes('post')
      ) {
        return {
          endpoint: '/api/orders/mark_shipped',
          payload: { type: 'india_post' }
        }
      }

      return {
        endpoint: '/api/orders/mark_shipped',
        payload: { type: 'other' }
      }
    },
    clearSubmitMessages () {
      this.submitErrorMessage = ''
      this.submitSuccessMessage = ''
    },
    async onCardAction (cardKey) {
      if (this.isCardActionLoading(cardKey)) return

      if (cardKey === 'ready_to_ship_count') {
        this.clearSubmitMessages()
        this.showDispatchConfirm = true
        return
      }

      this.clearSubmitMessages()
      this.activeCardActionKey = cardKey

      try {
        const type = this.cardActionType(cardKey)
        if (!type) return

        const response = await axios.post(`${this.$apiAdress}/api/orders/mark_shipped`, { type })
        const data = response?.data || {}
        if (data?.error) {
          this.submitErrorMessage = this.flattenErrorObject(data.error) || 'Unable to mark orders as shipped.'
          return
        }
        this.submitSuccessMessage = data?.message || 'Order status changed to shipped.'

        await this.fetchDashboardData()
      } catch (error) {
        const apiError = error?.response?.data?.error
        this.submitErrorMessage = this.flattenErrorObject(apiError) || error?.response?.data?.message || 'Unable to complete action right now.'
      } finally {
        this.activeCardActionKey = ''
      }
    },
    async confirmDispatch () {
      if (this.isCardActionLoading('ready_to_ship_count')) return
      this.activeCardActionKey = 'ready_to_ship_count'

      try {
        const response = await axios.get(`${this.$apiAdress}/api/shipping/generate_po_mail`, {
          responseType: 'blob'
        })

        const contentType = String(response?.headers?.['content-type'] || '').toLowerCase()
        if (contentType.includes('application/zip')) {
          const fallback = `Manakkal-PO-Dispatch-${new Date().toISOString().slice(0, 10)}.zip`
          const filename = this.extractAttachmentFilename(response?.headers?.['content-disposition']) || fallback
          this.triggerBlobDownload(response.data, filename)
          this.submitSuccessMessage = 'PO mail sent, orders marked as shipped, and Excel files downloaded.'
        } else {
          const text = await this.readBlobAsText(response?.data)
          this.submitSuccessMessage = text || 'PO mail generated successfully.'
        }

        this.showDispatchConfirm = false
        await this.fetchDashboardData()
      } catch (error) {
        this.submitErrorMessage = (await this.readBlobErrorMessage(error)) || 'Unable to generate PO mail right now.'
        this.showDispatchConfirm = false
      } finally {
        this.activeCardActionKey = ''
      }
    },
    cancelDispatch () {
      if (this.isCardActionLoading('ready_to_ship_count')) return
      this.showDispatchConfirm = false
    },
    triggerBlobDownload (blob, filename) {
      const url = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = filename
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
      setTimeout(() => window.URL.revokeObjectURL(url), 0)
    },
    extractAttachmentFilename (contentDisposition) {
      if (!contentDisposition) return ''
      const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(contentDisposition)
      return match ? decodeURIComponent(match[1]) : ''
    },
    async readBlobAsText (blob) {
      if (!blob) return ''
      if (typeof blob === 'string') return blob
      if (blob instanceof Blob) {
        try { return await blob.text() } catch (_) { return '' }
      }
      return ''
    },
    async readBlobErrorMessage (error) {
      const data = error?.response?.data
      if (data instanceof Blob) {
        const text = await this.readBlobAsText(data)
        try {
          const parsed = JSON.parse(text)
          return this.flattenErrorObject(parsed?.errors) || this.flattenErrorObject(parsed?.error) || parsed?.message || text
        } catch (_) {
          return text
        }
      }
      return this.flattenErrorObject(data?.error) || data?.message || ''
    },
    async markRowAsShipped (row, index) {
      if (this.isRowActionLoading(index)) return

      this.clearSubmitMessages()
      this.activeRowActionIndex = index

      try {
        const courier = row?.courier || row?.shipping_method || ''
        const requestConfig = this.deriveMarkShippedPayload(courier)
        const response = await axios.post(`${this.$apiAdress}${requestConfig.endpoint}`, requestConfig.payload)
        const data = response?.data || {}

        if (data?.error) {
          this.submitErrorMessage = this.flattenErrorObject(data.error) || 'Unable to mark orders as shipped.'
          return
        }

        this.submitSuccessMessage = data?.message || 'Order status changed to shipped.'
        await this.fetchDashboardData()
      } catch (error) {
        const apiError = error?.response?.data?.error
        this.submitErrorMessage = this.flattenErrorObject(apiError) || error?.response?.data?.message || 'Unable to mark orders as shipped right now.'
      } finally {
        this.activeRowActionIndex = null
      }
    },
    async fetchDashboardData () {
      this.isLoading = true
      this.errorMessage = ''

      try {
        const response = await axios.get(`${this.$apiAdress}/api/shipping_dashboard`)
        this.dashboardPayload = this.normalizePayload(response.data)
        this.lastUpdatedAt = new Date()
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || 'Unable to load shipping dashboard right now.'
      } finally {
        this.isLoading = false
      }
    },
    async submitTrackingUpdate () {
      this.submitErrorMessage = ''
      this.submitSuccessMessage = ''

      if (!this.manualTracking.orderNumber || !this.manualTracking.trackingId) {
        this.submitErrorMessage = 'Order ID and Tracking ID are required.'
        return
      }

      this.isSubmittingTracking = true

      try {
        const response = await axios.post(`${this.$apiAdress}/api/orders/update_tracking`, {
          order_number: this.manualTracking.orderNumber,
          tracking_id: this.manualTracking.trackingId
        })

        const data = response?.data || {}
        if (data && data.error) {
          this.submitErrorMessage = this.flattenErrorObject(data.error) || 'Unable to update tracking ID.'
          return
        }

        this.submitSuccessMessage = data?.message || 'Tracking ID updated successfully.'
        this.manualTracking.orderNumber = ''
        this.manualTracking.trackingId = ''
        await this.fetchDashboardData()
      } catch (error) {
        const apiError = error?.response?.data?.error
        this.submitErrorMessage = this.flattenErrorObject(apiError) || error?.response?.data?.message || 'Unable to update tracking ID right now.'
      } finally {
        this.isSubmittingTracking = false
      }
    },
    flattenErrorObject (errorPayload) {
      if (!errorPayload) return ''
      if (typeof errorPayload === 'string') return errorPayload

      if (typeof errorPayload === 'object') {
        return Object.values(errorPayload)
          .flat()
          .filter(Boolean)
          .join(' ')
      }

      return ''
    }
  }
}
</script>

<style scoped>
.shipping-dashboard-page {
  border-radius: 14px;
  background: linear-gradient(180deg, #f4f7fb 0%, #eef3fa 100%);
}

.shipping-dashboard-card {
  border-radius: 14px;
}

/* Filter / header bar */
.filter-bar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.page-meta {
  min-width: 0;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 800;
  color: #1f2a44;
  letter-spacing: -0.01em;
  margin: 0;
}

.page-subtitle {
  margin: 0.2rem 0 0;
  font-size: 0.8rem;
  color: #64748b;
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: center;
}

.meta-dot {
  color: #cbd5e1;
}

.filter-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.action-btn {
  height: 40px;
  min-width: 110px;
  padding: 0 1.1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  border-radius: 999px;
}

.scan-btn {
  background: var(--brand-gradient);
  background-size: 200% 100%;
  background-position: 0% 50%;
  border: none;
  color: #ffffff;
  gap: 0.5rem;
  letter-spacing: 0.02em;
  box-shadow: 0 8px 18px rgba(14, 91, 70, 0.28);
  transition: transform 0.18s ease, box-shadow 0.18s ease, background-position 0.4s ease;
}

.scan-btn:hover,
.scan-btn:focus,
.scan-btn:active {
  color: #ffffff;
  background: var(--brand-gradient);
  background-size: 200% 100%;
  background-position: 100% 50%;
  transform: translateY(-1px);
  box-shadow: 0 12px 24px rgba(14, 91, 70, 0.36);
}

.scan-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.35), 0 12px 24px rgba(14, 91, 70, 0.36);
}

.scan-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  position: relative;
}

.scan-icon::after {
  content: '';
  position: absolute;
  left: 4px;
  right: 4px;
  top: 50%;
  height: 2px;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 2px;
  box-shadow: 0 0 6px rgba(255, 255, 255, 0.7);
  animation: scan-line 1.6s ease-in-out infinite;
  pointer-events: none;
}

@keyframes scan-line {
  0%   { transform: translateY(-6px); opacity: 0.4; }
  50%  { transform: translateY(6px);  opacity: 1; }
  100% { transform: translateY(-6px); opacity: 0.4; }
}

/* Refresh button — outlined white, neutral, with rotating icon */
.refresh-btn {
  gap: 0.45rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.refresh-btn:hover:not(:disabled),
.refresh-btn:focus:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.refresh-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.refresh-btn:disabled {
  opacity: 1;
  background: #f8fafc;
  color: #4b5563;
  cursor: progress;
}

.refresh-btn__icon {
  flex-shrink: 0;
  display: block;
  color: #16a07a;
  transition: transform 0.4s ease;
}

.refresh-btn:hover:not(:disabled) .refresh-btn__icon {
  transform: rotate(60deg);
}

.refresh-btn.is-loading .refresh-btn__icon {
  animation: refresh-spin 0.9s linear infinite;
  color: #16a07a;
}

@keyframes refresh-spin {
  to { transform: rotate(360deg); }
}

/* Row gutters */
.tight-row {
  margin-left: -6px;
  margin-right: -6px;
  row-gap: 0.4rem;
}

.tight-row > [class*='col-'],
.tight-row > .col {
  padding-left: 6px;
  padding-right: 6px;
}

/* KPI tiles */
.tile-card {
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  color: #ffffff;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.10);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.tile-card::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.18), rgba(255, 255, 255, 0) 55%);
  pointer-events: none;
}

.tile-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.18);
}

.tile-body {
  padding: 1.1rem 1.15rem;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.tile-icon {
  position: absolute;
  top: 0.9rem;
  right: 0.95rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  color: #ffffff;
  opacity: 0.9;
}

.tile-icon .c-icon {
  width: 20px;
  height: 20px;
  color: #ffffff;
}

.tile-value {
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1.1;
  color: #ffffff;
  letter-spacing: -0.01em;
}

.tile-label {
  font-size: 0.78rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.92);
  letter-spacing: 0.01em;
}

.tile-action {
  align-self: flex-start;
  margin-top: 0.55rem;
  background: rgba(255, 255, 255, 0.20);
  border: 1px solid rgba(255, 255, 255, 0.40);
  color: #ffffff;
  padding: 0.32rem 0.8rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  outline: none;
  transition: all 0.18s ease;
  -webkit-tap-highlight-color: transparent;
}

.tile-action:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.30);
  border-color: rgba(255, 255, 255, 0.60);
}

.tile-action:focus-visible {
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.35);
}

.tile-action:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* Tile color variants */
.tile-slate    { background: linear-gradient(135deg, #1f2a44 0%, #34405e 100%); }
.tile-indigo   { background: linear-gradient(135deg, #2a2f8f 0%, #3949ab 100%); }
.tile-ocean    { background: linear-gradient(135deg, #2f80c8 0%, #4ea3e0 100%); }
.tile-cyan     { background: linear-gradient(135deg, #0892a5 0%, #1bb5c9 100%); }
.tile-violet   { background: linear-gradient(135deg, #6a1b9a 0%, #8e24aa 100%); }
.tile-emerald  { background: linear-gradient(135deg, #2e9d5f 0%, #43b97a 100%); }
.tile-amber    { background: linear-gradient(135deg, #e58e1a 0%, #f1ab3c 100%); }
.tile-rose     { background: linear-gradient(135deg, #c4495b 0%, #d96374 100%); }
.tile-crimson  { background: linear-gradient(135deg, #c0392b 0%, #e15543 100%); }

/* Reports + tabs */
.reports-section {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.report-tabs {
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
  padding: 0.25rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 999px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  width: fit-content;
  max-width: 100%;
  overflow-x: auto;
}

.report-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.1rem;
  border: none;
  outline: none;
  background: transparent;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.18s ease;
  -webkit-tap-highlight-color: transparent;
}

.report-tab:focus { outline: none; box-shadow: none; }
.report-tab:focus-visible { box-shadow: 0 0 0 3px rgba(14, 91, 70, 0.25); }

.report-tab:hover:not(.is-active) {
  background: #f1f5f9;
  color: #334155;
}

.report-tab.is-active {
  background: var(--brand-gradient);
  color: #ffffff;
  box-shadow: var(--brand-shadow);
}

.tab-count {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 0.12rem 0.5rem;
  border-radius: 999px;
  background: rgba(14, 91, 70, 0.10);
  color: var(--brand-color);
}

.report-tab.is-active .tab-count {
  background: rgba(255, 255, 255, 0.22);
  color: #ffffff;
}

/* Filter / search toolbar above the table */
.dashboard-filter-bar {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin: 0.5rem 0 0.75rem;
}

.dashboard-filter-bar__search {
  position: relative;
  flex: 1 1 280px;
  min-width: 220px;
  max-width: 420px;
  display: inline-flex;
  align-items: center;
}

.dashboard-filter-bar__search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  display: inline-flex;
  pointer-events: none;
}

.dashboard-filter-bar__input {
  width: 100%;
  height: 38px;
  padding: 0 2.4rem 0 2.2rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.85rem;
  color: #1f2a44;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.dashboard-filter-bar__input::placeholder {
  color: #9aa3b2;
}

.dashboard-filter-bar__input:focus {
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.dashboard-filter-bar__clear-input {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 999px;
  background: #eef2f7;
  color: #475569;
  font-size: 0.95rem;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.dashboard-filter-bar__clear-input:hover {
  background: #e2e8f0;
  color: #1f2a44;
}

.dashboard-filter-bar__org {
  flex: 0 0 240px;
  min-width: 200px;
}

.dashboard-filter-bar__clear {
  height: 38px;
  padding: 0 0.9rem;
  border: 1px solid #dc2626;
  background: #ffffff;
  color: #dc2626;
  font-size: 0.78rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.dashboard-filter-bar__clear:hover {
  border-color: #b91c1c;
  color: #ffffff;
  background: #dc2626;
  box-shadow: 0 2px 6px rgba(220, 38, 38, 0.25);
}

.dashboard-filter-bar__clear:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.25);
}

/* Inline row actions */
.row-action-btn {
  border-radius: 999px;
  padding: 0.25rem 0.85rem;
  font-size: 0.75rem;
  font-weight: 600;
}

/* Manual tracking card */
.tracking-card {
  border-radius: 12px;
}

.section-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1f2a44;
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  letter-spacing: -0.01em;
}

.section-accent {
  width: 4px;
  height: 18px;
  border-radius: 4px;
  background: var(--brand-gradient);
}

@media (max-width: 767px) {
  .filter-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-actions {
    width: 100%;
  }

  .action-btn {
    flex: 1;
  }

  .report-tabs {
    width: 100%;
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }

  .report-tabs::-webkit-scrollbar {
    display: none;
  }

  .report-tab {
    padding: 0.45rem 0.85rem;
    font-size: 0.8rem;
    flex: 0 0 auto;
  }

  .tab-count {
    font-size: 0.65rem;
    padding: 0.1rem 0.4rem;
  }
}

/* Shipping method / status pills — on-system with Orders.vue badge palette */
.ship-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.08rem 0.4rem;
  border-radius: 999px;
  font-size: 0.66rem;
  font-weight: 600;
  line-height: 1.35;
  white-space: nowrap;
}

.ship-badge__icon {
  width: 11px;
  height: 11px;
  flex-shrink: 0;
}

.ship-badge--green  { background: #dff5ea; color: #0e5b46; }
.ship-badge--amber  { background: #fef3c7; color: #92400e; }
.ship-badge--red    { background: #fee2e2; color: #991b1b; }
.ship-badge--blue   { background: #dbeafe; color: #1e40af; }
.ship-badge--sky    { background: #cffafe; color: #0e7490; }
.ship-badge--indigo { background: #e0e7ff; color: #3730a3; }
.ship-badge--violet { background: #ede9fe; color: #5b21b6; }
.ship-badge--gray   { background: #f1f5f9; color: #475569; }
</style>
